import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, Globe, MapPin } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

const getResourceTags = (resource) => {
  const haystack = `${resource.type} ${resource.description}`.toLowerCase();
  const tags = [];

  if (haystack.includes('food') || haystack.includes('meal')) tags.push('Food');
  if (haystack.includes('legal')) tags.push('Legal');
  if (haystack.includes('counselling') || haystack.includes('mental')) tags.push('Counselling');
  if (haystack.includes('housing') || haystack.includes('shelter')) tags.push('Housing');
  if (haystack.includes('hiv') || haystack.includes('prep')) tags.push('HIV Care');
  if (haystack.includes('sexual health') || haystack.includes('testing') || haystack.includes('sti')) tags.push('Sexual Health');
  if (haystack.includes('lgbt') || haystack.includes('queer') || haystack.includes('trans')) tags.push('2SLGBTQ+');
  if (haystack.includes('community')) tags.push('Community');
  if (haystack.includes('crisis')) tags.push('Crisis');

  return Array.from(new Set(tags));
};

const ResourcesPage = () => {
  const navigate = useNavigate();
  const [selectedCity, setSelectedCity] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const resources = [
    // Ottawa Resources
    {
      name: 'Max Ottawa',
      description: 'Sexual health services, HIV testing, and LGBTQ+ support for gay, bi, queer, and trans men',
      type: 'Health & Support',
      city: 'Ottawa',
      contact: '613-233-7440',
      website: 'https://www.maxottawa.ca/'
    },
    {
      name: 'AIDS Committee of Ottawa (ACO)',
      description: 'HIV/AIDS education, support services, harm reduction programs, and community outreach',
      type: 'HIV Support',
      city: 'Ottawa',
      contact: '613-238-5014',
      website: 'https://aco-cso.ca/'
    },
    {
      name: 'Bruce House',
      description: 'Supportive housing and services for people living with HIV/AIDS in Ottawa',
      type: 'Housing & Support',
      city: 'Ottawa',
      contact: '613-729-0911',
      website: 'https://brucehouse.org/'
    },
    {
      name: 'Anchor',
      description: 'Community support, harm reduction services, and crisis intervention',
      type: 'Harm Reduction',
      city: 'Ottawa',
      contact: '613-238-8210',
      website: '#'
    },
    {
      name: 'Get a Kit',
      description: 'Free HIV and STI self-testing kits delivered discreetly to your door',
      type: 'Testing',
      city: 'Ottawa',
      contact: 'Order online',
      website: 'https://www.getakit.ca/'
    },
    {
      name: 'Ottawa Public Health - Sexual Health Clinic',
      description: 'Free STI testing, treatment, PrEP consultations, and sexual health information',
      type: 'Health Clinic',
      city: 'Ottawa',
      contact: '613-234-4641',
      website: 'https://www.ottawapublichealth.ca/'
    },
    {
      name: 'Ottawa Food Bank',
      description: 'Food bank network with groceries and food provisions across Ottawa.',
      type: 'Food Bank',
      city: 'Ottawa',
      contact: 'foodbank@ottawafoodbank.ca',
      website: 'https://ottawafoodbank.ca/'
    },
    {
      name: 'Dalhousie Food Cupboard',
      description: 'Free groceries and food provisions. Usually once per month with proof of address and photo ID.',
      type: 'Food Bank',
      city: 'Ottawa',
      contact: '613-230-3982',
      website: 'https://dalhousiefoodcupboard.ca/'
    },
    {
      name: 'Centre 507',
      description: 'Meals 7 days a week plus recreation and community resources.',
      type: 'Meals & Community Support',
      city: 'Ottawa',
      contact: '613-233-5626',
      website: 'https://centre507.org/'
    },
    {
      name: 'Legal Aid Ottawa',
      description: 'Legal advice, lawyers, and certificates for qualifying issues.',
      type: 'Legal Support',
      city: 'Ottawa',
      contact: '613-238-7931',
      website: 'https://www.legalaid.on.ca/'
    },
    {
      name: 'Counselling Connect',
      description: 'Free phone or video counselling in English, French, and Arabic.',
      type: 'Mental Health',
      city: 'Ottawa',
      contact: 'Visit website',
      website: 'https://www.counsellingconnect.org/'
    },
    {
      name: 'Mild2Wild',
      description: 'A supportive online space for 2SGBTQ+ guys who Party and Play (PnP). Bi-weekly Zoom drop-in group for guys across Canada who party and play, use chemsex, or use substances to enhance intimate play.',
      type: 'Community Support',
      city: 'National',
      contact: 'Bi-weekly Zoom drop-in group',
      website: ''
    },
    {
      name: 'Parkdale Food Centre',
      description: 'Food bank support through appointments or drop-in hours.',
      type: 'Food Bank',
      city: 'Ottawa',
      contact: '613-722-8019',
      website: 'https://parkdalefoodcentre.ca/'
    },
    {
      name: 'The Ottawa Mission',
      description: 'Daily meals and crisis support services.',
      type: 'Meals & Community Support',
      city: 'Ottawa',
      contact: '613-234-1144',
      website: 'https://ottawamission.com/'
    },
    {
      name: 'Community Legal Services of Ottawa (Downtown)',
      description: 'Legal help with housing, income, disability, refugee, immigration, tribunals, and employment issues.',
      type: 'Legal Support',
      city: 'Ottawa',
      contact: '613-241-7008',
      website: 'https://www.clsottawa.ca/'
    },
    {
      name: 'The Walk-In Counselling Clinic',
      description: 'Free in-person or virtual counselling at multiple Ottawa locations.',
      type: 'Mental Health',
      city: 'Ottawa',
      contact: 'Visit website',
      website: 'https://walkincounselling.com/'
    },
    {
      name: 'Bruce House',
      description: 'Housing and support for people living with HIV in Ottawa.',
      type: 'Housing',
      city: 'Ottawa',
      contact: '613-729-0911',
      website: 'https://brucehouse.ca/'
    },
    {
      name: 'Capital Rainbow Refuge',
      description: 'Support for LGBTQI+ refugees and newcomers, including housing, legal, and mental health connections.',
      type: '2SLGBTQ+ Housing Support',
      city: 'Ottawa',
      contact: 'info@capitalrainbow.ca',
      website: 'https://capitalrainbow.ca/'
    },
    {
      name: 'The PrEP Clinic',
      description: 'STI/HIV testing, treatment, PrEP prescriptions, and free HPV vaccination.',
      type: 'Sexual Health',
      city: 'Ottawa',
      contact: '416-420-1250',
      website: 'https://www.prepclinic.ca/'
    },
    // Toronto Resources
    {
      name: 'ACT (AIDS Committee of Toronto)',
      description: 'HIV/AIDS education, prevention, support services, and community programs for people living with and affected by HIV',
      type: 'HIV Support',
      city: 'Toronto',
      contact: '416-340-2437',
      website: 'https://www.actoronto.org/'
    },
    {
      name: 'GMSH (Gay Men\'s Sexual Health Alliance)',
      description: 'Sexual health promotion, HIV/STI prevention, and community programs for gay, bi, queer, and trans men',
      type: 'Sexual Health',
      city: 'Toronto',
      contact: 'Visit website',
      website: 'https://gmsh.ca/'
    },
    {
      name: 'Hassle Free Clinic',
      description: 'Anonymous HIV/STI testing, sexual health services, and trans health care',
      type: 'Health Clinic',
      city: 'Toronto',
      contact: '416-922-0566',
      website: 'https://hasslefreeclinic.org/'
    },
    {
      name: 'Toronto People With AIDS Foundation (PWA)',
      description: 'Practical support services, advocacy, and community programs for people living with HIV/AIDS',
      type: 'HIV Support',
      city: 'Toronto',
      contact: '416-506-1400',
      website: 'https://www.pwatoronto.org/'
    },
    {
      name: 'Casey House',
      description: 'Specialized hospital and community-based care for people living with HIV/AIDS',
      type: 'Health Care',
      city: 'Toronto',
      contact: '416-962-7600',
      website: 'https://www.caseyhouse.com/'
    },
    {
      name: 'HIV & AIDS Legal Clinic (HALCO)',
      description: 'Legal aid, advocacy, and rights information for people living with or affected by HIV in Ontario',
      type: 'Legal Support',
      city: 'Toronto',
      contact: '416-340-7790',
      website: 'https://www.halco.org/'
    },
    {
      name: 'The 519',
      description: 'LGBTQ+ community centre with counseling, support groups, and harm reduction programs',
      type: 'Community Centre',
      city: 'Toronto',
      contact: '416-392-6874',
      website: 'https://www.the519.org/'
    },
    // National Resources
    {
      name: 'NORS Canada',
      description: 'National Overdose Response Service - Virtual safe consumption with 24/7 peer support',
      type: 'Harm Reduction',
      city: 'National',
      contact: '1-888-688-6677',
      website: 'https://www.nors.ca/'
    },
    {
      name: 'CATIE',
      description: 'Canada\'s source for HIV and hepatitis C information, resources, and training',
      type: 'Information',
      city: 'National',
      contact: '1-800-263-1638',
      website: 'https://www.catie.ca/'
    },
    {
      name: 'CBRC',
      description: 'National queer and trans health research, education, and community resources',
      type: '2SLGBTQ+ Health',
      city: 'National',
      contact: 'Visit website',
      website: 'https://www.cbrc.net/'
    }
  ];

  const cities = ['all', 'Ottawa', 'Toronto', 'National'];
  const resourceTypes = ['all', ...Array.from(new Set(resources.flatMap((resource) => getResourceTags(resource))))];
  const filteredResources = resources.filter((resource) => {
    const cityMatches = selectedCity === 'all' || resource.city === selectedCity;
    const typeMatches = selectedType === 'all' || getResourceTags(resource).includes(selectedType);
    const searchMatches = searchQuery.trim() === '' || [resource.name, resource.description, resource.type, resource.city]
      .join(' ')
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    return cityMatches && typeMatches && searchMatches;
  });

  const getPrimaryGroup = (resource) => {
    const tags = getResourceTags(resource);
    if (tags.includes('Food')) return 'Food & Meals';
    if (tags.includes('HIV Care') || tags.includes('Sexual Health')) return 'Clinics & HIV Care';
    if (tags.includes('Legal')) return 'Legal Help';
    if (tags.includes('Housing')) return 'Housing';
    if (tags.includes('Counselling') || tags.includes('Crisis')) return 'Mental Health & Crisis';
    if (tags.includes('2SLGBTQ+')) return '2SLGBTQ+ Community';
    return 'Community Support';
  };

  const groupedResources = filteredResources.reduce((groups, resource) => {
    const groupName = getPrimaryGroup(resource);
    groups[groupName] = groups[groupName] || [];
    groups[groupName].push(resource);
    return groups;
  }, {});

  const orderedGroups = [
    'Food & Meals',
    'Clinics & HIV Care',
    'Legal Help',
    'Housing',
    'Mental Health & Crisis',
    '2SLGBTQ+ Community',
    'Community Support'
  ].filter((groupName) => groupedResources[groupName]?.length);

  return (
    <div className="min-h-screen flex flex-col relative z-10">
      {/* Header */}
      <header className="glass-morphism sticky top-0 z-50 border-b">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/')}
              data-testid="back-button"
              className="mr-4 text-[rgb(var(--text-secondary))] hover:text-[rgb(var(--primary))]"
            >
              <ArrowLeft size={24} weight="duotone" />
            </Button>
            <div className="flex items-center space-x-2">
              <span className="text-3xl">🌍</span>
              <h1 className="text-2xl font-bold text-gradient">
                Community Resources
              </h1>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-8 pb-24">
        <p className="text-[rgb(var(--text-secondary))] mb-6 leading-relaxed">
          Support services for 2SLGBTQ+ folks across Canada. All services are confidential and judgment-free. 💜
        </p>

        <div className="mb-6" data-testid="resources-search-section">
          <p className="text-xs uppercase tracking-wider text-[rgb(var(--text-secondary))] mb-3">Search by service or name</p>
          <input
            type="text"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Try: food bank, legal aid, counselling, PrEP, housing..."
            data-testid="resources-search-input"
            className="w-full rounded-2xl border border-white/10 bg-[rgb(var(--surface))] px-4 py-3 text-sm text-[rgb(var(--text-primary))] placeholder:text-[rgb(var(--text-secondary))] focus:outline-none focus:border-[rgb(var(--primary))]/40"
          />
        </div>

        {/* City Filter */}
        <div className="flex flex-wrap gap-2 mb-6">
          {cities.map((city) => (
            <button
              key={city}
              onClick={() => setSelectedCity(city)}
              data-testid={`filter-${city}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                selectedCity === city
                  ? 'bg-[rgb(var(--primary))] text-white'
                  : 'bg-[rgb(var(--surface))] text-[rgb(var(--text-secondary))] hover:bg-[rgb(var(--surface-elevated))] border border-white/10'
              }`}
            >
              {city === 'all' ? 'All Cities' : city}
            </button>
          ))}
        </div>

        <div className="mb-6">
          <p className="text-xs uppercase tracking-wider text-[rgb(var(--text-secondary))] mb-3">Filter by support type</p>
          <div className="flex flex-wrap gap-2">
            {resourceTypes.map((type) => (
              <button
                key={type}
                onClick={() => setSelectedType(type)}
                data-testid={`type-filter-${type.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedType === type
                    ? 'bg-[rgb(var(--secondary))] text-white'
                    : 'bg-[rgb(var(--surface))] text-[rgb(var(--text-secondary))] hover:bg-[rgb(var(--surface-elevated))] border border-white/10'
                }`}
              >
                {type === 'all' ? 'All types' : type}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-8" data-testid="resources-list">
          {orderedGroups.map((groupName) => (
            <section key={groupName} data-testid={`resource-group-${groupName.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`}>
              <h3 className="text-sm font-medium uppercase tracking-wider text-[rgb(var(--text-secondary))] mb-4">{groupName}</h3>
              <div className="space-y-4">
                {groupedResources[groupName].map((resource, index) => (
                  <div
                    key={`${groupName}-${resource.name}-${index}`}
                    data-testid={`resource-${groupName.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}-${index}`}
                    className="bg-[rgb(var(--surface))] hover:bg-[rgb(var(--surface-elevated))] rounded-2xl p-6 border border-white/5 transition-all duration-200 hover:border-[rgb(var(--primary))]/20"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-[rgb(var(--text-primary))] mb-2">
                          {resource.name}
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-[rgb(var(--primary))]/10 text-[rgb(var(--primary))] border border-[rgb(var(--primary))]/20">
                            {resource.type}
                          </span>
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[rgb(var(--accent))]/10 text-[rgb(var(--accent))] border border-[rgb(var(--accent))]/20">
                            <MapPin size={12} weight="bold" className="mr-1" />
                            {resource.city}
                          </span>
                          {getResourceTags(resource).map((tag) => (
                            <span
                              key={`${resource.name}-${tag}`}
                              data-testid={`resource-tag-${tag.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`}
                              className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-white/5 text-[rgb(var(--text-secondary))] border border-white/10"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    <p className="text-[rgb(var(--text-secondary))] mb-4 leading-relaxed">
                      {resource.description}
                    </p>

                    <div className="space-y-2">
                      <div className="flex items-center text-sm text-[rgb(var(--text-secondary))]">
                        <Phone size={16} weight="duotone" className="mr-2 text-[rgb(var(--primary))]" />
                        {resource.contact}
                      </div>
                      {resource.website ? (
                        <a
                          href={resource.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`resource-link-${groupName.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}-${index}`}
                          className="flex items-center text-sm text-[rgb(var(--primary))] hover:text-[rgb(var(--accent))] transition-colors"
                        >
                          <Globe size={16} weight="duotone" className="mr-2" />
                          Visit Website
                        </a>
                      ) : (
                        <div className="flex items-center text-sm text-[rgb(var(--text-secondary))]">
                          <Globe size={16} weight="duotone" className="mr-2" />
                          Online drop-in / ask organizer
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>

        {/* Emergency Info */}
        <div className="mt-8 p-6 bg-[rgb(var(--danger))]/10 border border-[rgb(var(--danger))]/20 rounded-2xl">
          <h3 className="text-lg font-semibold text-[rgb(var(--danger))] mb-2">
            Crisis Hotlines - Call Anytime
          </h3>
          <div className="space-y-3 mt-4">
            <a href="tel:911" className="flex items-center justify-between p-3 bg-[rgb(var(--background))] hover:bg-[rgb(var(--surface-elevated))] rounded-xl transition-all">
              <div>
                <div className="font-semibold text-[rgb(var(--danger))] flex items-center">
                  911 <span className="ml-2 px-2 py-0.5 text-xs bg-[rgb(var(--danger))]/20 rounded-full">EMERGENCY</span>
                </div>
                <p className="text-xs text-[rgb(var(--text-secondary))]">Police, Fire, Ambulance</p>
              </div>
              <Phone size={20} weight="duotone" className="text-[rgb(var(--danger))]" />
            </a>
            
            <a href="tel:613-722-6914" className="flex items-center justify-between p-3 bg-[rgb(var(--background))] hover:bg-[rgb(var(--surface-elevated))] rounded-xl transition-all">
              <div>
                <div className="font-semibold text-[rgb(var(--text-primary))]">Ottawa Crisis Line</div>
                <p className="text-xs text-[rgb(var(--text-secondary))]">613-722-6914 • 24/7</p>
              </div>
              <Phone size={20} weight="duotone" className="text-[rgb(var(--primary))]" />
            </a>
            
            <a href="tel:211" className="flex items-center justify-between p-3 bg-[rgb(var(--background))] hover:bg-[rgb(var(--surface-elevated))] rounded-xl transition-all">
              <div>
                <div className="font-semibold text-[rgb(var(--text-primary))]">211 Mental Health</div>
                <p className="text-xs text-[rgb(var(--text-secondary))]">Community Services • 24/7</p>
              </div>
              <Phone size={20} weight="duotone" className="text-[rgb(var(--primary))]" />
            </a>
            
            <a href="tel:613-238-8210" className="flex items-center justify-between p-3 bg-[rgb(var(--background))] hover:bg-[rgb(var(--surface-elevated))] rounded-xl transition-all">
              <div>
                <div className="font-semibold text-[rgb(var(--text-primary))]">Anchor</div>
                <p className="text-xs text-[rgb(var(--text-secondary))]">613-238-8210 • Ottawa Crisis Support</p>
              </div>
              <Phone size={20} weight="duotone" className="text-[rgb(var(--primary))]" />
            </a>
            
            <a href="tel:416-929-5200" className="flex items-center justify-between p-3 bg-[rgb(var(--background))] hover:bg-[rgb(var(--surface-elevated))] rounded-xl transition-all">
              <div>
                <div className="font-semibold text-[rgb(var(--text-primary))]">Gerstein Crisis Centre</div>
                <p className="text-xs text-[rgb(var(--text-secondary))]">416-929-5200 • Toronto 24/7</p>
              </div>
              <Phone size={20} weight="duotone" className="text-[rgb(var(--primary))]" />
            </a>
            
            <a href="tel:1-866-531-2600" className="flex items-center justify-between p-3 bg-[rgb(var(--background))] hover:bg-[rgb(var(--surface-elevated))] rounded-xl transition-all">
              <div>
                <div className="font-semibold text-[rgb(var(--text-primary))]">ConnexOntario</div>
                <p className="text-xs text-[rgb(var(--text-secondary))]">1-866-531-2600 • Mental Health & Addictions</p>
              </div>
              <Phone size={20} weight="duotone" className="text-[rgb(var(--primary))]" />
            </a>
            
            <a href="tel:1-833-456-4566" className="flex items-center justify-between p-3 bg-[rgb(var(--background))] hover:bg-[rgb(var(--surface-elevated))] rounded-xl transition-all">
              <div>
                <div className="font-semibold text-[rgb(var(--text-primary))]">988 Suicide Crisis Helpline</div>
                <p className="text-xs text-[rgb(var(--text-secondary))]">Call or text 988 • National 24/7</p>
              </div>
              <Phone size={20} weight="duotone" className="text-[rgb(var(--primary))]" />
            </a>
          </div>
          <p className="text-xs text-[rgb(var(--text-secondary))] mt-4 text-center">All calls are confidential</p>
        </div>
      </main>
    </div>
  );
};

export default ResourcesPage;